<template>
  <CustomTable ref="table" class="business-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
    <template v-slot:header-options>
      <div class="sub-filters">
        <button v-for="(subFilter, index) in subFilters" :key="subFilter.label" class="sub-filter-button" :class="{ active: subFilterIndex === index }" @click="setSubFilter(index)">{{ subFilter.label }}</button>
      </div>
    </template>
    <template v-slot:table>
      <thead>
        <tr>
          <custom-header field="depositNumber" class="col-fit">Acompte</custom-header>
          <custom-header field="number" class="hide-for-mobile col-fit">Devis</custom-header>
          <custom-header field="title" class="hide-for-mobile col-fill">Titre</custom-header>
          <custom-header field="depositStatus" class="hide-for-mobile col-fit">Status</custom-header>
          <custom-header field="clientName" class="col-fit">Client</custom-header>
          <custom-header field="deposit" class="hide-for-mobile col-fit">Montant</custom-header>
          <custom-header field="depositRemaining" class="col-fit">Reste à encaisser</custom-header>
        </tr>
      </thead>
      <tbody v-if="!isLoading && data && data.length > 0">
        <tr v-for="(business, index) in $refs.table.$refs.searchArray.simpleFilter(data)" :key="index" class="business-row" @click="openBusiness(business)">
          <custom-body field="depositNumber" class="col-fit">
            <DocumentChips :deposit-number="business.depositNumber" />
          </custom-body>
          <custom-body field="number" class="hide-for-mobile col-fit">
            <QuoteNumber :number="business.number" :order-number="haveProductionOption ? business.orderNumber : undefined" />
          </custom-body>
          <custom-body field="title" class="hide-for-mobile col-fill" :title="business.title">{{ business.title ? business.title : '' }}</custom-body>
          <custom-body field="depositStatus" class="hide-for-mobile col-fit"><StatusBadge :status="business.depositStatus" :status-enum="DEPOSIT_STATUS_ENUM" /></custom-body>
          <custom-body field="clientName" class="col-fit cell-strong col-truncate" :title="clientNameOf(business)">{{ clientNameOf(business) }}</custom-body>
          <custom-body field="deposit" class="hide-for-mobile col-fit cell-strong">{{ formatAmount(business.deposit) }}</custom-body>
          <custom-body field="depositRemaining" class="col-fit">{{ business.depositRemaining ? formatAmount(business.depositRemaining) : '-' }}</custom-body>
        </tr>
      </tbody>
      <tbody v-else><tr class="empty-row">
        <td colspan="7" class="hide-for-mobile">Aucune facture d'acompte</td>
      </tr></tbody>
    </template>
  </CustomTable>
  <BusinessManagementModal ref="businessManagementModal" :callback=loadData />
</template>

<script>
import {BusinessController} from "@/controllers/business/BusinessController";
import BusinessManagementModal from "@/components/modals/BusinessManagementModal.vue";
import CustomTable from "@/components/CustomTable.vue";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import StatusBadge from "@/components/tables/commons/StatusBadge.vue";
import DocumentChips from "@/components/tables/commons/DocumentChips.vue";
import QuoteNumber from "@/components/tables/commons/QuoteNumber.vue";
import {DEPOSIT_STATUS_ENUM} from "@/models/depositStatusEnum";
import {BUSINESS_DOCUMENT_TYPE_ENUM} from "@/models/businessDocumentTypeEnum";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {FILTER_OPERATOR_ENUM} from "@/models/filterOperatorEnum";
import {CustomTableService} from "@/services/CustomTableService";
import {COUNTS_REFRESH_EVENT} from "@/components/businessTabs";

// Filtre secondaire « non payés / payés » : le premier est sélectionné par défaut, un clic sur le filtre actif le désélectionne
// (aucun sous-filtre = tous les acomptes, dont les annulés). NOT_IN et non IN pour « non payés » : les anciens acomptes n'ont pas
// de statut enregistré (ils sont considérés comme émis).
const SUB_FILTERS = [
  {label: "Non payés", filter: {field: "depositStatus", operator: FILTER_OPERATOR_ENUM.NOT_IN.value, variable: [DEPOSIT_STATUS_ENUM.PAID.value, DEPOSIT_STATUS_ENUM.ABORTED.value]}},
  {label: "Payés", filter: {field: "depositStatus", operator: FILTER_OPERATOR_ENUM.IN.value, variable: [DEPOSIT_STATUS_ENUM.PAID.value]}},
];

/**
 * Tableau des factures d'acompte : une ligne par contrat ayant un acompte. Un clic ouvre la modale du contrat directement sur l'acompte.
 * Le back renvoie toutes les lignes (pas de pagination).
 */
export default {
  name: 'DepositTable',
  components: {CustomBody, CustomHeader, CustomTable, StatusBadge, DocumentChips, QuoteNumber, BusinessManagementModal},
  computed: {
    DEPOSIT_STATUS_ENUM() {
      return DEPOSIT_STATUS_ENUM;
    },
    haveProductionOption() {
      return localStorage && localStorage.options && localStorage.options.includes('production');
    }
  },
  data() {
    return {
      data: [],
      elements: [
        {label: "Date de l'acompte", value: "depositCreationDate", type: "date"},
        {label: "N°Acompte", value: "depositNumber", type: "number"},
        {label: "N°Devis", value: "number", type: "number"},
        {label: "N°Commande", value: "orderNumber", type: "number"},
        {label: "Titre", value: "title", type: "text"},
        {label: "Status", value: "depositStatus", type: "select", values: DEPOSIT_STATUS_ENUM},
        {label: "Client", value: "clientName", type: "text"},
        {label: "Montant", value: "deposit", type: "number"},
        {label: "Reste à encaisser", value: "depositRemaining", type: "number", sortOnly: true},
      ],
      searchOptions: {
        filters: [],
        sorts: [{field: "depositNumber", order: SORT_OPERATOR_ENUM.DESCENDING.value}]
      },
      subFilters: SUB_FILTERS,
      subFilterIndex: -1,
      isLoading: true,
      hasLoadedOnce: false
    };
  },
  async mounted() {
    this.$refs.table.$refs.searchArray.initWithURLParams();

    this.$refs.table.$refs.paginationArray.paginationInputs.pages.readOnly = true;
    this.$refs.table.$refs.paginationArray.isNextReloadCancel = true;
    this.$refs.table.$refs.paginationArray.paginationInputs.pages.value = undefined;
    this.$refs.table.$refs.paginationArray.index = undefined;

    // Initialisation uniquement : le premier sous-filtre est sélectionné par défaut
    this.applySubFilter(0);

    await this.loadData();

    // Clean URL without reload
    const url = new URL(window.location.href);
    window.history.replaceState({}, '', url.origin + url.pathname);
  },
  methods: {
    // Clic utilisateur : cliquer sur le sous-filtre actif le désélectionne (-1 = aucun sous-filtre = tous les acomptes)
    setSubFilter(index) {
      this.applySubFilter(index === this.subFilterIndex ? -1 : index);
      this.loadData();
    },
    // Retire le sous-filtre appliqué puis pose celui d'index newIndex dans les filtres du tableau (modifiés sur place :
    // c'est cette liste que lit buildURLParams() pour la requête)
    applySubFilter(newIndex) {
      const filters = CustomTableService.data.get(this.$refs.table.tableId).currentFilters;
      const isSameFilter = (a, b) => a.field === b.field && a.operator === b.operator && JSON.stringify(a.variable) === JSON.stringify(b.variable);
      const previous = this.subFilterIndex >= 0 ? this.subFilters[this.subFilterIndex].filter : undefined;
      const next = newIndex >= 0 ? this.subFilters[newIndex].filter : undefined;

      if (previous) {
        for (let i = filters.length - 1; i >= 0; i--) {
          if (isSameFilter(filters[i], previous)) filters.splice(i, 1);
        }
      }
      if (next) filters.push({...next});

      this.subFilterIndex = newIndex;
    },
    clientNameOf(business) {
      return business.currentClientName ? business.currentClientName : (business.clientName ? business.clientName : '');
    },
    formatAmount(value) {
      return value ? value.toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €' : '';
    },
    async loadData() {
      this.$refs.table?.startLoading();
      this.isLoading = true;
      this.data = [];

      const resp = await BusinessController.getDepositTable(this.$refs.table.buildURLParams());
      if (resp && !resp.errors) {
        this.data.push(...resp);
      }

      this.isLoading = false;
      this.$refs.table?.stopLoading();
      // Les compteurs du menu ne sont recalculés que si les données ont changé (pas à l'ouverture de la rubrique)
      if (this.hasLoadedOnce) window.dispatchEvent(new Event(COUNTS_REFRESH_EVENT));
      this.hasLoadedOnce = true;
    },
    // Le tableau ne reçoit que les champs affichés : la modale a besoin du contrat complet
    async openBusiness(business) {
      if (!business.id) return;
      const fullBusiness = await BusinessController.get(business.id);
      if (fullBusiness && !fullBusiness.errors) {
        this.$refs.businessManagementModal.openModal(fullBusiness, {type: BUSINESS_DOCUMENT_TYPE_ENUM.DEPOSIT.value, creditNoteNumber: null});
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';
</style>
